import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Skeleton } from '../components/ui/skeleton';
import { Flame, Music, Play, BarChart3 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const rankColor = (rank) =>
    rank === 1 ? 'text-[#7FFF00]' : rank === 2 ? 'text-amber-400' : rank === 3 ? 'text-amber-600' : 'text-zinc-500';

const ChartList = ({ title, icon, accent, rows, loading }) => (
    <div className="glass-panel rounded-sm overflow-hidden">
        <div className="flex items-center gap-2 p-4 border-b border-white/10" style={{ background: 'rgba(0,0,0,.3)' }}>
            {icon}
            <h2 className="font-display font-bold text-lg" style={{ color: accent }}>{title}</h2>
        </div>
        {loading ? (
            <div className="divide-y divide-white/5">
                {Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 p-3">
                        <Skeleton className="h-6 w-6 bg-white/5 rounded" />
                        <Skeleton className="h-4 flex-1 bg-white/5" />
                        <Skeleton className="h-4 w-10 bg-white/5" />
                    </div>
                ))}
            </div>
        ) : rows.length > 0 ? (
            <div className="divide-y divide-white/5">
                {rows.map((p) => (
                    <Link
                        key={p.performance_id}
                        to={`/performance/${p.performance_id}`}
                        className="flex items-center gap-3 p-3 hover:bg-white/5 transition-colors group"
                    >
                        <div className={`w-7 text-center font-mono font-bold text-lg ${rankColor(p.rank)}`}>{p.rank}</div>
                        <div className="flex-1 min-w-0">
                            <p className="font-semibold truncate group-hover:text-primary transition-colors">{p.title}</p>
                            <p className="text-xs text-zinc-500 truncate">
                                {p.artist_name}{p.location ? ` · ${p.location}` : ''} · {p.judgment_count} judge{p.judgment_count === 1 ? '' : 's'}
                            </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                            <span className="font-mono font-bold text-base text-white">{(p.overall_score || 0).toFixed(1)}</span>
                            <span className="text-[10px] text-zinc-500">/10</span>
                        </div>
                        <Play className="w-4 h-4 text-zinc-600 group-hover:text-primary transition-colors flex-shrink-0" />
                    </Link>
                ))}
            </div>
        ) : (
            <div className="text-center py-12 px-4">
                <Music className="w-10 h-10 mx-auto text-zinc-700 mb-3" />
                <p className="text-zinc-500 text-sm">No ranked {title.toLowerCase()} yet — needs judged performances.</p>
            </div>
        )}
    </div>
);

const ChartsPage = () => {
    const [limit, setLimit] = useState(10);
    const [songs, setSongs] = useState([]);
    const [freestyles, setFreestyles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);
        Promise.all([
            axios.get(`${API}/charts/songs?limit=${limit}`),
            axios.get(`${API}/charts/freestyles?limit=${limit}`),
        ])
            .then(([s, f]) => {
                if (!active) return;
                setSongs(s.data);
                setFreestyles(f.data);
            })
            .catch((e) => console.error('Failed to fetch charts:', e))
            .finally(() => active && setLoading(false));
        return () => { active = false; };
    }, [limit]);

    return (
        <div className="max-w-5xl mx-auto px-4 py-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="font-display font-bold text-2xl sm:text-3xl flex items-center gap-3">
                        <BarChart3 className="w-8 h-8 text-[#7FFF00]" />
                        THE <span className="text-[#7FFF00]">CHARTS</span>
                    </h1>
                    <p className="text-zinc-400 text-sm mt-1">
                        Top performances by skill score — earned through judgments, never plays.
                    </p>
                </div>
                <div className="flex items-center gap-1 glass-panel rounded-sm p-1">
                    {[10, 25].map((n) => (
                        <button
                            key={n}
                            onClick={() => setLimit(n)}
                            className={`px-4 py-1.5 rounded-sm text-sm font-bold font-mono transition-colors ${
                                limit === n ? 'bg-primary text-white' : 'text-zinc-400 hover:text-white'
                            }`}
                        >
                            TOP {n}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
                <ChartList title="Top Songs" icon={<Music className="w-5 h-5 text-[#2979FF]" />} accent="#2979FF" rows={songs} loading={loading} />
                <ChartList title="Top Freestyles" icon={<Flame className="w-5 h-5 text-[#FF3DCB]" />} accent="#FF3DCB" rows={freestyles} loading={loading} />
            </div>
        </div>
    );
};

export default ChartsPage;
